import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UtilDialogService } from '@core';
import { DocSubmitTypeEnum, Program, ProgramSupplier, ProgramTypeEnum, Supplier } from '@models';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';

@Component({
  selector: 'prox-program-suppliers-sub',
  templateUrl: './program-suppliers.sub.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-suppliers-sub {
        .p-tabpanels {
          padding: 0 !important;
        }
      }

      prox-program-suppliers-sub {
        .p-splitter {
          background: none !important;
        }
      }

      prox-program-suppliers-sub {
        .p-floatlabel {
          font-size: 24px;
          font-weight: bold;
        }
      }

      prox-program-suppliers-sub {
        prox-field label {
          margin-left: 5px;
          font-weight: 600;
          font-size: 1rem;
        }
      }
    `,
  ],
})
export class ProgramSuppliersSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  suppliers: Supplier[] = [];
  programSuppliers: ProgramSupplier[] = [];
  selectedProgramSupplier?: ProgramSupplier;
  inventoryPrograms: Program[] = [];

  // supplierGridOptions: GridOptionsExt = {};
  supplierChecklist: { supplier: Supplier; isChecked: boolean }[] = [];

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  get manifests() {
    return this.program?.programBillingConfigs.filter((x) => x.manifestId != null).map((x) => x.manifest);
  }

  async ngOnInit() {
    this.programSuppliers = this.program.programSuppliers;
    this.selectedProgramSupplier = _.first(this.programSuppliers);

    const programs = await this.dbQueryService.getPrograms(this.program.accountId);
    this.inventoryPrograms = programs.filter((p) => p.programTypeId === ProgramTypeEnum.Inventory && p.id !== this.program.id);

    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);
    // check for new suppliers
    this.programSuppliers = await this.parent.createMissingProgramSuppliers();
    // AgFns.sizeColumnsToFit(this.supplierGridOptions);
  }

  isReadOnly() {
    return this.parent.isReadOnly();
  }

  getSupplierAdminName(): string {
    if (!this.selectedProgramSupplier) return '';
    const sa = this.parent.account.supplierAccounts.find(
      (sa) => sa.supplierId === this.selectedProgramSupplier!.supplierId,
    );
    return sa?.assignedSupplierAdmin?.name ?? '';
  }

  getSupplierAdminEmail(): string | undefined {
    if (!this.selectedProgramSupplier) return undefined;
    const sa = this.parent.account.supplierAccounts.find(
      (sa) => sa.supplierId === this.selectedProgramSupplier!.supplierId,
    );
    return sa?.assignedSupplierAdmin?.proximityUser?.email ?? undefined;
  }

  sendEmailToSupplierAdmin() {
    const email = this.getSupplierAdminEmail();
    if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  }

  getSupplierAccountAllowsInventory(): boolean {
    if (!this.selectedProgramSupplier) return false;
    const sa = this.parent.account.supplierAccounts.find(
      (sa) => sa.supplierId === this.selectedProgramSupplier!.supplierId,
    );
    return sa?.allowInventory ?? false;
  }

  /** Human-readable invoice delivery method from the matching SupplierAccount. Read-only display. */
  getSupplierInvoiceDeliveryLabel(): string {
    if (!this.selectedProgramSupplier) return '';
    const sa = this.parent.account.supplierAccounts.find(
      (sa) => sa.supplierId === this.selectedProgramSupplier!.supplierId,
    );
    if (!sa) return '';
    switch (sa.invoiceDocSubmitTypeId) {
      case DocSubmitTypeEnum.Print: return 'Print';
      case DocSubmitTypeEnum.Email: return 'Email';
      case DocSubmitTypeEnum.PrintAndEmail: return 'Print & Email';
      case DocSubmitTypeEnum.None: return 'None';
      default: return '';
    }
  }

  /** Default invoice email from the matching SupplierAccount. Falls back to '(none)' when empty. */
  getSupplierDefaultInvoiceEmail(): string {
    if (!this.selectedProgramSupplier) return '';
    const sa = this.parent.account.supplierAccounts.find(
      (sa) => sa.supplierId === this.selectedProgramSupplier!.supplierId,
    );
    return sa?.invoiceEmail?.trim() || '(none)';
  }

  onAllowInventoryChanged() {
    if (this.selectedProgramSupplier && !this.selectedProgramSupplier.allowInventory) {
      this.selectedProgramSupplier.inventoryProgramId = undefined;
    }
  }
}
